import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@veracity/vui'
import { useNavigate } from 'react-router-dom'

import { projectsClient, scenariosClient } from '@/api'
import { AddScenarioRequest } from '@/interfaces'

const baseRoute = '/projects'
const BASE_QUERY_KEY = projectsClient.QUERY_KEY

export const useProjectsDetails = (projectId: number) => {
  const QUERY_KEY = [...BASE_QUERY_KEY, projectId]

  const { showSuccess } = useToast()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const getProjectDetailsQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => projectsClient.getById(projectId)
  })

  const updateProjectMutation = useMutation({
    mutationFn: (updatedName: string) =>
      projectsClient.updateProjectName(projectId, updatedName),
    onSuccess: async () => {
      showSuccess('Project successfully updated')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  const removeProjectMutation = useMutation({
    mutationFn: () => projectsClient.removeById(projectId),
    onSuccess: () => {
      showSuccess('Project successfully removed')
      navigate(baseRoute)
    }
  })

  const addScenarioMutation = useMutation({
    mutationFn: (req: Omit<AddScenarioRequest, 'projectId'>) =>
      scenariosClient.createScenario(projectId, req),
    onSuccess: (newScenarioId: number) => {
      showSuccess('Scenario successfully created')
      navigate(`${baseRoute}/${projectId}/scenarios/${newScenarioId}`)
    }
  })

  return {
    projectInfo: getProjectDetailsQuery.data,
    isFetching: getProjectDetailsQuery.isFetching,
    updateProject: updateProjectMutation.mutate,
    isUpdateProjectInProgress: updateProjectMutation.isPending,
    removeProject: removeProjectMutation.mutate,
    addScenario: addScenarioMutation.mutate,
    isAddScenarioInProgress: addScenarioMutation.isPending
  }
}
